import type {
  Point,
  TransformState,
} from "../../../../../types/stage-types.js";

/**
 * Cursor tracker configuration options
 */
export interface CursorConfig {
  readonly tracking?: boolean;
  readonly container?: HTMLElement;
}

/**
 * Cursor state interface
 */
interface CursorState {
  readonly tracking: boolean;
  readonly stagePosition: Point | null;
  readonly productPosition: Point | null;
  readonly currentTransform: TransformState;
}

/**
 * CursorTracker API for managing cursor position tracking and coordinate conversion
 */
export interface CursorTrackerAPI {
  readonly updatePosition: (position: Point | null) => void;
  readonly updateTransform: (transform: TransformState) => void;
  readonly convertToProduct: (stagePosition: Point) => Point;
  readonly calculateOrigin: () => Point;
  readonly setTracking: (tracking: boolean) => void;
  readonly getState: () => Readonly<CursorState>;
  readonly destroy: () => void;
}

/**
 * Creates a CursorTracker for handling cursor position tracking and coordinate conversion
 * @param container - Container element for coordinate calculations
 * @param config - Cursor tracker configuration options
 * @returns CursorTracker API
 */
export const createCursorTracker = (
  container: HTMLElement,
  config: CursorConfig = {}
): CursorTrackerAPI => {
  console.log("👆 Creating CursorTracker");

  let isDestroyed = false;

  // Configuration with defaults
  const cursorConfig = {
    tracking: config.tracking !== false, // Default to true
    container: config.container || container,
  };

  // Cursor state
  let state: CursorState = {
    tracking: cursorConfig.tracking,
    stagePosition: null,
    productPosition: null,
    currentTransform: {
      scale: 1,
      translateX: 0,
      translateY: 0,
      rotation: 0,
    },
  };

  /**
   * Calculate the coordinate origin based on product container position and scale
   * @returns Point representing the true origin for coordinate system
   */
  const calculateCoordinateOrigin = (): Point => {
    // Find the product container element
    const productContainer = document.querySelector(
      ".stage__product-container"
    ) as HTMLElement;

    if (!productContainer) {
      // Fallback to simple transform if container not found
      return {
        x: state.currentTransform.translateX,
        y: state.currentTransform.translateY,
      };
    }

    const containerRect = productContainer.getBoundingClientRect();
    const stageRect = container.getBoundingClientRect();

    // Calculate container position relative to stage
    const containerLeft = containerRect.left - stageRect.left;
    const containerTop = containerRect.top - stageRect.top;

    // Origin = container position / scale
    return {
      x: containerLeft / state.currentTransform.scale,
      y: containerTop / state.currentTransform.scale,
    };
  };

  /**
   * Convert cursor position from stage coordinates to product coordinates
   * @param stagePosition - Cursor position in stage coordinates
   * @returns Position in product coordinate system
   */
  const convertToProductCoordinates = (stagePosition: Point): Point => {
    const origin = calculateCoordinateOrigin();

    // Product coordinates = (stage position - origin) / scale
    const productX =
      (stagePosition.x - origin.x * state.currentTransform.scale) /
      state.currentTransform.scale;
    const productY =
      (stagePosition.y - origin.y * state.currentTransform.scale) /
      state.currentTransform.scale;

    return { x: productX, y: productY };
  };

  /**
   * Update cursor position
   * @param position - Current cursor position in stage coordinates (null to clear)
   */
  const updatePosition = (position: Point | null): void => {
    if (isDestroyed || !state.tracking) return;

    const productPosition = position
      ? convertToProductCoordinates(position)
      : null;

    state = {
      ...state,
      stagePosition: position,
      productPosition,
    };
  };

  /**
   * Update transform state for coordinate calculations
   * @param transform - New transform state
   */
  const updateTransform = (transform: TransformState): void => {
    if (isDestroyed) return;

    state = {
      ...state,
      currentTransform: { ...transform },
    };

    // Recalculate product position if we have a stage position
    if (state.stagePosition) {
      const productPosition = convertToProductCoordinates(state.stagePosition);
      state = {
        ...state,
        productPosition,
      };
    }
  };

  /**
   * Set tracking state
   * @param tracking - Whether to track cursor position
   */
  const setTracking = (tracking: boolean): void => {
    if (isDestroyed) return;

    state = {
      ...state,
      tracking,
    };

    // Clear positions if tracking is disabled
    if (!tracking) {
      state = {
        ...state,
        stagePosition: null,
        productPosition: null,
      };
    }
  };

  /**
   * Get current cursor state
   */
  const getState = (): Readonly<CursorState> => ({ ...state });

  /**
   * Public method to access coordinate origin calculation
   */
  const calculateOrigin = (): Point => calculateCoordinateOrigin();

  /**
   * Public method to access coordinate conversion
   */
  const convertToProduct = (stagePosition: Point): Point =>
    convertToProductCoordinates(stagePosition);

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Clear state
    state = {
      tracking: false,
      stagePosition: null,
      productPosition: null,
      currentTransform: {
        scale: 1,
        translateX: 0,
        translateY: 0,
        rotation: 0,
      },
    };
  };

  console.log("CursorTracker initialized");

  return {
    updatePosition,
    updateTransform,
    convertToProduct,
    calculateOrigin,
    setTracking,
    getState,
    destroy,
  };
};
